import type { FC } from 'react'
import { useCallback } from 'react'
import { useSetAtom, useAtomValue } from 'jotai'
import { onDragStartAtom, onDragEndAtom } from './store/dnd.store'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { DndContext, pointerWithin } from '@dnd-kit/core'
import { DraggingCueOverlay } from './ui/dragging-cue-overlay'
import { DraggableCue } from './ui/draggable-cue'
import { NextHardStartCueOverlay } from './ui/next-hard-start-cue-overlay'
import {
  visibleIndexedCuesAtom,
  getIndexedCueAtom,
} from '../../store/rundown.store'
import { GRID_DOM_BODY } from '../rundown/grid/grid.const'

type CuesListProps = {
  onDragEnd: (event: DragEndEvent) => void
}

const CuesList: FC<CuesListProps> = ({ onDragEnd }) => {
  const visibleCuesMeta = useAtomValue(visibleIndexedCuesAtom)
  const getCueMeta = useAtomValue(getIndexedCueAtom)
  const setDragStart = useSetAtom(onDragStartAtom)
  const setDragEnd = useSetAtom(onDragEndAtom)

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const cue = getCueMeta(active.id as string)

      if (!cue) return

      setDragStart({
        cue,
      })
    },
    [getCueMeta, setDragStart],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      onDragEnd(event)
      setDragEnd()
    },
    [onDragEnd, setDragEnd],
  )

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      <div
        id={GRID_DOM_BODY}
        role="rowgroup"
        className="w-full min-w-max relative"
      >
        {visibleCuesMeta.map(({ id }) => {
          return <DraggableCue key={id} cueId={id} />
        })}
      </div>

      <NextHardStartCueOverlay />
      <DraggingCueOverlay />
    </DndContext>
  )
}

export default CuesList
